import {inject, injectable} from "inversify";
import {Class, Entity} from "tick-knock";
import {IBuilding} from "@game/coregame/interfaces/IBuilding";
import {log} from "@dp-instant/utils";
import {di, lazyGet, WaitAction} from "@dp-instant/core";
import {TypesGame} from "@game/TypesGame";
import {CapitalView} from "@game/coregame/view/CapitalView";
import {StateShapeView} from "@game/coregame/view/StateShapeView";
import {Population} from "@game/coregame/view/Population";
import {Spawner} from "@game/coregame/view/Spawner";
import {Fighter} from "@game/coregame/ecs/entities/Fighter";
import {PlayerType} from "@game/coregame/interfaces/PlayerType";
import {GameModel} from "@game/coregame/model/GameModel";
import {BotLogic} from "@game/coregame/ecs/components/BotLogic";
import {PathHolder} from "@game/coregame/ecs/components/PathHolder";
import {Graphics, InteractionEvent, Text} from "pixi.js-legacy";
import {GameColors} from "@game/coregame/settings";
import {color} from "@game/coregame/helpers";
import {BuildingModel} from "@game/coregame/interfaces/BuildingModel";
import {Vector2} from "@game/coregame/interfaces/Vector2";
import {IVibrationManager, TypesSocial} from "@dp-instant/interfaces";
import {hapticHeavy, hapticBattle} from "@game/haptic";

@injectable()
export class Building extends Entity {
    public static readonly ACTIVE_TAG = "active";
    @inject(TypesGame.model)
    private model: GameModel;

    private _stateId: string;
    private _selected = false;
    private _owner: PlayerType;
    private _data: IBuilding;
    @inject(TypesGame.views.state)
    private _capital: CapitalView;
    @inject(TypesGame.views.stateShape)
    private _shape: StateShapeView;
    @inject(TypesGame.views.population)
    private _population: Population;
    // @inject(Spawner)
    private _spawner: Spawner;
    private _pathHolder: PathHolder = new PathHolder();

    init(id: string, data: IBuilding): this {
        this.addTag(Building.ACTIVE_TAG);
        this._data = data;
        this._stateId = id;
        this._spawner ||= di.get(Spawner);
        this._spawner.selfBuilding = this;
        this._population.init(data);
        this._capital.init(data);
        this._shape.init(data, id);

        this._shape.interactive = true;
        if (process.env.NODE_ENV === "development") {
            this._shape.on("pointerdown", (e: InteractionEvent) => {
                if (this.hasTag(Building.ACTIVE_TAG) && e.data.originalEvent.shiftKey) {
                    const bounds = this._shape["_container"].getLocalBounds();
                    const boundsGraphics = new Graphics()
                        .lineStyle(2, 0)
                        .drawRect(bounds.x, bounds.y, bounds.width, bounds.height)
                        .endFill();
                    this._shape.addChild(boundsGraphics);
                    const idLabel = new Text(id, {fontSize: "16", fill: "#900"});
                    this._population.addChild(idLabel);
                    idLabel.x = idLabel.width * -0.5;
                    idLabel.y = -30;

                    WaitAction.frame(20).then(() => {
                        idLabel.parent?.removeChild(idLabel);
                        boundsGraphics.parent?.removeChild(boundsGraphics);
                        const building = this;
                        log.info(building.stateId);
                    });
                }
            });
        }
        this.addMainComponents();

        this.updatePopulationRateAndLimit();
        return this;
    }

    sendTo(building: Building) {
        this._spawner.spawnAndSend(this._population.current, building);
    }

    setInactive(value: boolean) {
        if (value) {
            this.removeTag(Building.ACTIVE_TAG);
            this.get(Spawner)?.stopRoutine();
            this.removeComponent(PathHolder);
            this.removeComponent(Spawner);
        } else {
            this.addTag(Building.ACTIVE_TAG);
            this.addMainComponents();
        }
        this.updateAllSkins();
        this.onComponentListUpdated();
    }
    public toggleActive(stage?: number, duration?: number) {
        const active = isNaN(<number>stage) || this.hasTag(Building.ACTIVE_TAG);
        this._shape.setActive(active, duration);
        this._capital.setActive(active, duration);
        this._population.active = active;
    }
    setStartOwner(owner: PlayerType) {
        this.owner = owner;
        this._population.current = this.model.meta.getStartPopulation(owner);
        this.updatePopulationRateAndLimit();
    }

    tryOccupy(fighter: Fighter) {
        const population = this.get<Population>(Population);
        const spawner = this.get(Spawner)!;

        if (population) {
            population.block();

            if (this._owner == fighter.owner) {
                population.current += fighter.amount;
            } else {
                const capital = this.get<CapitalView>(CapitalView);
                capital?.shake(0.3, 0.2);

                if (fighter.owner == PlayerType.First) {
                    hapticBattle();
                    // _vibrationManager.VibratePeekTimeout();
                    const vibrationManager = lazyGet<IVibrationManager>(
                        TypesSocial.vibrationManager
                    );
                    vibrationManager?.vibrate(); // vibrate
                }

                const removed = population.remove(fighter.amount);
                if (removed == 0) {
                    if (fighter.owner == PlayerType.First) hapticHeavy();
                    capital?.occupiedAnimation(0.2);
                    population.current += Population.SPAWN_AMOUNT_ON_OCCUPATION;
                    this.owner = fighter.owner;
                    spawner.stopRoutine();

                    if (this._selected) {
                        this.deselect();
                    }
                }
            }
        }
    }

    selectAsTarget() {
        const [hex, alpha] = this.isFirstPlayer ? GameColors.aims.allied : GameColors.aims.enemy;
        this.showSelection(color.fromHex(hex), alpha);
    }
    selectAsSource() {
        this._selected = true;
        const [hex, alpha] = GameColors.aims.allied;
        this.showSelection(color.fromHex(hex), alpha);
    }
    private showSelection(color?: number, alpha = 0.5) {
        const capital = this.get(CapitalView);
        capital?.showSelection(color, alpha);
    }

    private updatePopulationRateAndLimit() {
        const population = this.get<Population>(Population);
        if (population) {
            population.setPopulationLimit(this.model.meta.getBuildingPopulationLimit(this._owner));
            population.setPopulationRate(this.model.meta.getPopulationRate(this._owner));
            this.get(StateShapeView)?.updateWithPopulation(population); // TODO: remove this
        }
    }

    deselect() {
        this._selected = false;
        this.showSelection(NaN);
    }

    public get selected(): boolean {
        return this._selected;
    }

    get isFirstPlayer(): boolean {
        return this._owner === PlayerType.First;
    }

    get stateId(): string {
        return this._stateId;
    }

    get owner(): PlayerType {
        return this._owner;
    }

    set owner(value: PlayerType) {
        this._owner = value;

        this.addMainComponents();
        this.updateAllSkins();

        switch (value) {
            case PlayerType.Default:
                [Spawner, PathHolder, BotLogic].forEach((component: Class<any>) =>
                    this.remove(component)
                );
                break;
            case PlayerType.Neutral:
            case PlayerType.First:
                this.remove(BotLogic);
                break;
            default:
                this.add(di.get<BotLogic>(TypesGame.botLogic));
                break;
        }
        this.onComponentListUpdated();
        this.updatePopulationRateAndLimit();
    }

    private onComponentListUpdated() {
        this._population.visible = this.has(Population);
        this._capital.visible = this.has(CapitalView);
    }

    public getSnapshot(): BuildingModel {
        const now = Date.now();
        const startTimestamp = 0;

        const population: Population | undefined = this.get(Population);
        const spawner: Spawner | undefined = this.get(Spawner);
        const pathHolder: PathHolder | undefined = this.get(PathHolder);
        return {
            Owner: this._owner,
            SpawnRate: this.model.meta.getPopulationRate(this._owner),
            AttackRate: 0,
            AttackRadius: 0,
            Id: this.stateId,
            Position: this._data.statePos.slice(0) as Vector2,
            CurrentPopulation: population?.current || 0,
            SpawnLimit: this.model.meta.getBuildingPopulationLimit(this._owner),
            FighterSpeed: Fighter.NORMAL_SPEED,
            LastActionTimestamp: startTimestamp - (now - (population?.lastBlockTimestamp || 0)),
            BurstDelay: spawner?.burstDelay || 0,
            BurstWidth: pathHolder?.getPathWidth() || 0
        };
    }
    public updateAllSkins() {
        const isActive = this.hasTag(Building.ACTIVE_TAG);
        this.get(StateShapeView)?.updateSkin(this._owner);
        this.get(CapitalView)?.updateSkin(this._owner, isActive);
    }

    private addMainComponents() {
        const mainComponents: any[] = [
            this._capital,
            this._spawner,
            this._shape,
            this._population,
            this._pathHolder
        ];
        if (!this.hasAll(...mainComponents)) {
            mainComponents.forEach(comp => this.add(comp));
        }
    }

    get data(): IBuilding {
        return this._data;
    }

    public destroy() {
        // TODO:
    }
}
